import { ChatBotState, ChatParams } from "src/model/chatParamsModel";
import { METRIC_NAMESPACE, METRIC_UNIT_COUNT, URL_PATH_DIMENSION } from "src/metrics/metricsConsts";
import Cookies from 'js-cookie';

export interface StringToAnyMap {
    [key: string]: any;
}
export class CommonUtils {
    static readonly UNKNOWN = 'UNKNOWN';

    /**
     * Return is current environment Production or not
     */
    public static isProduction() {
        return process.env.NODE_ENV === 'production';
    }

    /**
     * Substitute string with UNKNOWN if string is undefined or  null
     * @param value string to validate
     */
    public static invalidStringHandler(value: string | undefined): string {
        if (value) {
            return value;
        }
        return this.UNKNOWN;
    }

    /**
     * Convert window attribute to string map, this is required for extracting value from window in TS.
     * @param key Key name to extract from window.
     * @param refWindow Optionally extract value from provided reference window
     */
    public static getWindowValue(key: string, refWindow?: Window): any {
        if(refWindow) {
            return (refWindow as StringToAnyMap)[key];
        }
        return (window as StringToAnyMap)[key];
    }

    /**
     * Get requestId stored in window attribute
     */
    public static getRequestId(): string {
        return this.getWindowValue('ue_id');
    }

    /**
     * Get parent/source requestId stored in window.parent attribute
     */
    public static getParentRequestId(): string {
        return this.getWindowValue('ue_id', window.parent);
    }

    /**
     * Get session id of the user from Cookies if present otherwise return UNKNOWN
     */
    static getClientSessionId() {
        return Cookies.get('session-id') || CommonUtils.UNKNOWN;
    }

    public static getCurrentPageIdentifier(pageType: string): string {
        return `${pageType}::${window.location.href}`
    }

    /**
     * Convert any JSON config to URL encoded state and encode it to Base64 using btoa
     * @param config
     */
    public static convertConfigToUrlParam(config: any): string{
        return btoa(unescape(encodeURIComponent(JSON.stringify(config))));
    }

    /**
     * Convert Url Params to Config after decoding from Base64
     * @param urlParams: UrlSearchParams
     */

    public static convertUrlParamsToConfig(urlParamKey: string, urlParam: URLSearchParams): any {
        if(urlParam.get(urlParamKey)) {
            return JSON.parse(decodeURIComponent(
                escape(atob(urlParam.get(urlParamKey) as string))
            ));
        }
        return {};
    }

    /**
     * Get the Config to pass as chatBot params with Empty Client ID.
     */
    public static getEmptyChatBotParamConfig(): ChatParams {
        return {
            data: {
                workflowId: '',
                clientId: '',
                isRVV: false,
                conversationHeader: {
                    stringId: 'eu-vsp-abc-dynamic-toaster-cr',
                    defaultValue: 'Amazon Business Coach'
                }
            },
            chatBotState: ChatBotState.MINIMIZE,
        }
    }

    /**
     * Helper function to return EMF format for a given metric name and dimension
     * @param metricName Metric Name
     * @param dimensions Dimensions for the metric
     */
    public static createEmfLogs(metricName: string, dimensions: Record<string, string>) {
        return {
            _aws: {
                Timestamp: Date.now(),
                CloudWatchMetrics: [
                    {
                        Namespace: METRIC_NAMESPACE,
                        Dimensions: [Object.keys(dimensions)],
                        Metrics: [
                            {
                                Name: metricName,
                                Unit: METRIC_UNIT_COUNT,
                            },
                        ],
                    },
                ],
            },
            [metricName]: 1,
            ...dimensions
        };
    }

    /**
     * Find if the current page is either an exact match or starts with based on list of passed paths.
     * @param currentPagePath
     * @param PathsList
     * @return Boolean Path match found or not.
     */
    public static isPathPresent(currentPagePath: string, PathsList: string[]): boolean {
        let foundMatch: boolean = false;
        PathsList.forEach((path: string) => {
            // Exact url path and partial matching with startsWith
            if(path === currentPagePath || currentPagePath.startsWith(path)){
                foundMatch = true;
            }
        });
        return foundMatch;
    }

    /**
     * Check if widget with provided identifier is rendered on the page or not.
     * @param widget_id
     */
    public static isWidgetRendered(widget_id: string): boolean {
        return document.getElementById(widget_id) != undefined;
    }
}
